import { ArrowDownToLine, Linkedin, Mail } from "lucide-react";

import { profile } from "@/data/profile";
import { useLocale } from "@/i18n/locale";
import { ui } from "@/i18n/ui";
import { useConsent } from "@/lib/consent-context";

export function Contact() {
  const { t } = useLocale();
  const { reopen } = useConsent();

  return (
    <footer id="contact" className="border-t border-border py-16 sm:py-24">
      <div className="mx-auto w-full max-w-5xl px-6">
        <h2 className="label mb-10 text-accent">{t(ui.contact)}</h2>

        <p className="max-w-2xl text-2xl font-medium tracking-tight text-balance sm:text-3xl">
          {t(profile.cta)}
        </p>

        <ul className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
          <li>
            <a
              href={`mailto:${profile.email}`}
              className="inline-flex items-center gap-2 text-sm text-muted transition-colors hover:text-accent"
            >
              <Mail aria-hidden="true" className="size-4" />
              {profile.email}
            </a>
          </li>
          <li>
            <a
              href={profile.linkedin}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-2 text-sm text-muted transition-colors hover:text-accent"
            >
              <Linkedin aria-hidden="true" className="size-4" />
              {profile.linkedinLabel}
            </a>
          </li>
          <li>
            <a
              href={profile.resumePath}
              download
              className="inline-flex items-center gap-2 text-sm text-muted transition-colors hover:text-accent"
            >
              <ArrowDownToLine aria-hidden="true" className="size-4" />
              {t(ui.resumePdf)}
            </a>
          </li>
        </ul>

        <div className="mt-10">
          <h3 className="label mb-4 text-faint">{t(ui.github)}</h3>
          <ul className="flex flex-wrap gap-x-8 gap-y-3">
            {profile.github.map((account) => (
              <li key={account.url}>
                <a
                  href={account.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="group inline-flex items-baseline gap-2 text-sm text-muted transition-colors hover:text-accent"
                >
                  {account.label}
                  {/* Suppressed when the handle IS the label (HookSys), where
                      repeating it reads as a rendering bug rather than detail. */}
                  {account.handle !== account.label && (
                    <span className="font-mono text-xs text-faint transition-colors group-hover:text-muted">
                      {account.handle}
                    </span>
                  )}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-16 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-6">
          <p className="font-mono text-xs text-faint">
            © {new Date().getFullYear()} {profile.name}
          </p>
          {/* A consent decision has to be reversible, or "Decline" is a trap. */}
          <button
            type="button"
            onClick={reopen}
            className="font-mono text-xs text-faint underline-offset-4 transition-colors hover:text-accent hover:underline"
          >
            {t(ui.cookieSettings)}
          </button>
        </div>
      </div>
    </footer>
  );
}
